import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ui/ConfirmDialog';
import {
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Landmark,
  ExternalLink,
  Unlink,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';

interface StripeAccountStatus {
  account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  onboarding_completed: boolean;
  country: string;
  default_currency: string;
}

const StripeConnectSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState<StripeAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [hasAccessError, setHasAccessError] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    if (!user?.organization?.id) {
      setError(t('settings.stripeConnect.errors.notOrganizer'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setHasAccessError(false);
      const res = await apiService.get(`/stripe/connect/status/${user.organization.id}`);
      setStatus(res.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // Pas de compte Connect
        setStatus(null);
      } else {
        const errorMessage = err.response?.data?.detail || t('settings.stripeConnect.errors.loadStatus');
        setError(errorMessage);

        // Détecter les erreurs d'accès au compte Stripe (clés test/prod incompatibles)
        if (errorMessage.includes('does not have access to account') ||
            errorMessage.includes('Application access may have been revoked')) {
          setHasAccessError(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnboard = async () => {
    if (!user?.organization?.id) return;

    try {
      setActionLoading(true);
      setError(null);
      const res = await apiService.post('/stripe/connect/onboard', {
        organization_id: user.organization.id,
        return_url: `${window.location.origin}/admin/settings?stripe_onboarding=success&tab=stripe`,
        refresh_url: `${window.location.origin}/admin/settings?stripe_onboarding=refresh&tab=stripe`,
      });

      // Rediriger vers Stripe Onboarding
      window.location.href = res.data.onboarding_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.stripeConnect.errors.onboarding'));
      setActionLoading(false);
    }
  };

  const handleDashboard = async () => {
    if (!user?.organization?.id) return;

    try {
      setActionLoading(true);
      setError(null);
      const res = await apiService.get(`/stripe/connect/dashboard-link/${user.organization.id}`);

      // Ouvrir dans nouvel onglet
      window.open(res.data.url, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.stripeConnect.errors.dashboard'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = () => {
    if (!user?.organization?.id) return;
    setDisconnectConfirmOpen(true);
  };

  const confirmDisconnect = async () => {
    if (!user?.organization?.id) return;
    setDisconnectConfirmOpen(false);

    try {
      setActionLoading(true);
      setError(null);

      await apiService.post('/stripe/connect/disconnect', {
        organization_id: user.organization.id,
        reason: 'user_request'
      });

      setSuccess(t('settings.stripeConnect.disconnectSuccess'));
      setTimeout(() => setSuccess(null), 5000);

      // Recharger le statut
      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.stripeConnect.errors.disconnect'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <CreditCard size={24} className="text-muted-foreground" />
        {t('settings.stripeConnect.title')}
      </h2>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline">{t('common.close')}</button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6 border-success/40 bg-success/10">
          <AlertDescription className="flex items-center justify-between text-success">
            {success}
            <button onClick={() => setSuccess(null)} className="text-sm underline">{t('common.close')}</button>
          </AlertDescription>
        </Alert>
      )}

      {status?.onboarding_completed ? (
        // Compte actif
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="text-success" size={32} />
              <div className="flex-1">
                <h3 className="text-lg font-bold">{t('settings.stripeConnect.accountActive')}</h3>
              </div>
              <Badge className="bg-success/20 text-success border-success/40">{t('settings.stripeConnect.operational')}</Badge>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              {t('settings.stripeConnect.accountActiveDescription')}
            </p>

            <Separator className="my-4" />

            <h4 className="text-sm font-bold mb-4">{t('settings.stripeConnect.accountInfo')}</h4>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('settings.stripeConnect.chargesEnabled')}</span>
                <Badge className={status.charges_enabled ? 'bg-success/20 text-success border-success/40' : ''}>
                  {status.charges_enabled ? t('common.yes') : t('common.no')}
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('settings.stripeConnect.payoutsEnabled')}</span>
                <Badge className={status.payouts_enabled ? 'bg-success/20 text-success border-success/40' : ''}>
                  {status.payouts_enabled ? t('common.yes') : t('common.no')}
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('settings.stripeConnect.country')}</span>
                <span className="text-sm">{status.country}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('settings.stripeConnect.currency')}</span>
                <span className="text-sm">{status.default_currency.toUpperCase()}</span>
              </div>
            </div>

            <Separator className="my-4" />

            <Button
              variant="outline"
              onClick={handleDashboard}
              disabled={actionLoading}
              className="mb-4"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ExternalLink size={18} className="mr-2" />
              )}
              {t('settings.stripeConnect.accessDashboard')}
            </Button>

            <Alert className="border-info/30 bg-info/[0.06]">
              <AlertDescription className="text-sm">
                <strong>{t('settings.stripeConnect.payoutsAutomatic')}</strong> {t('settings.stripeConnect.payoutsInfo')}
              </AlertDescription>
            </Alert>

            {/* Bouton de dissociation */}
            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">{t('settings.stripeConnect.dangerZone')}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                disabled={actionLoading}
                className="text-destructive border-destructive hover:bg-destructive/10"
              >
                <Unlink size={16} className="mr-2" />
                {t('settings.stripeConnect.disconnectStripe')}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                {t('settings.stripeConnect.historyPreserved')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Onboarding incomplet
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="text-warning" size={32} />
              <div className="flex-1">
                <h3 className="text-lg font-bold">{t('settings.stripeConnect.configurationIncomplete')}</h3>
              </div>
              <Badge className="bg-warning/20 text-warning border-warning/40">{t('settings.stripeConnect.actionRequired')}</Badge>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              {t('settings.stripeConnect.onboardingDescription')}
            </p>

            <Alert className="mb-6 border-warning/40 bg-warning/10">
              <AlertDescription className="text-sm">
                <p className="font-semibold mb-2">{t('settings.stripeConnect.onboardingRequired')}</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('settings.stripeConnect.onboardingIban')}</li>
                  <li>{t('settings.stripeConnect.onboardingInfo')}</li>
                  <li>{t('settings.stripeConnect.onboardingKyc')}</li>
                </ul>
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleOnboard}
              disabled={actionLoading}
              size="lg"
              className="mb-2"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Landmark size={18} className="mr-2" />
              )}
              {t('settings.stripeConnect.completeProfile')}
            </Button>

            <p className="text-xs text-muted-foreground">
              {t('settings.stripeConnect.redirectInfo')}
            </p>

            {/* Bouton de dissociation si erreur d'accès */}
            {hasAccessError && (
              <div className="mt-6 pt-4 border-t">
                <Alert className="mb-4 border-warning/40 bg-warning/10">
                  <AlertDescription className="text-sm">
                    <p className="font-semibold mb-2 text-warning">{t('settings.stripeConnect.compatibilityIssue')}</p>
                    <p className="text-warning">
                      {t('settings.stripeConnect.compatibilityDescription')}
                    </p>
                  </AlertDescription>
                </Alert>

                <p className="text-xs text-muted-foreground mb-2">{t('settings.stripeConnect.dangerZone')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={actionLoading}
                  className="text-destructive border-destructive hover:bg-destructive/10"
                >
                  <Unlink size={16} className="mr-2" />
                  {t('settings.stripeConnect.disconnectOldAccount')}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('settings.stripeConnect.afterDisconnect')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informations supplémentaires */}
      <Card className="mt-6 bg-muted/50">
        <CardContent className="pt-6">
          <h4 className="text-sm font-bold mb-3">{t('settings.stripeConnect.aboutTitle')}</h4>

          <p className="text-sm text-muted-foreground mb-3">
            {t('settings.stripeConnect.aboutDescription')}
          </p>

          <p className="text-sm text-muted-foreground">
            <strong>{t('settings.stripeConnect.commissionTitle')}</strong> {t('settings.stripeConnect.commissionDescription')}
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={disconnectConfirmOpen}
        onOpenChange={setDisconnectConfirmOpen}
        title={t('settings.stripeConnect.disconnectTitle')}
        message={t('settings.stripeConnect.disconnectConfirm')}
        confirmLabel={t('settings.stripeConnect.disconnectAction')}
        destructive
        onConfirm={confirmDisconnect}
      />
    </div>
  );
};

export default StripeConnectSettings;
